// "Perspectives: The brooding mind": many women → hair into threads → one thinking mind → a held pause.
const P = (name, th, color) => ({ src: `/arcs/brooding/${name}.webp`, th, color });

export const broodingMind = {
  slug: "the-brooding-mind",
  duration: 24,
  stages: [
    { id: "women", at: 0, hint: "many perspectives" },
    { id: "threads", at: 4.2, hint: "" },
    { id: "one", at: 8, hint: "one thinking mind" },
    { id: "pause", at: 15, hint: "a pause before the next step" },
  ],
  line: { text: "Despite our differences, the thinking mind is full of empathy and colours of emotion.", from: 9.5, to: 23, pos: "low" },
  figures: {
    list: [P("braids", -1.75, "#E0338F"), P("bun", -1.15, "#8CC63F"), P("curls", -0.55, "#F07A1A"), P("headscarf", 0.55, "#2B5BC8"), P("locs", 1.15, "#E2475A"), P("plait", 1.75, "#1FA3A0")],
    appear: [0.2, 3],
    lag: 0.3,
    dur: 1.3,
    thread: [4, 7.5],
    fade: [7.2, 10],
  },
  ambient: { scene: "hum", until: 99 },
  look: { start: 0.02, lift: [0, 1], end: 0.02, yaw: [[0, 0.9], [1.8, 1.75], [4.6, -1.75], [6.6, -0.6], [8.2, 0], [24, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "profiles", pos: "low" },
  speed: [[0, 0.3], [9, 0.3], [15, 0.06], [24, 0.04]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
uniform float uFigTh[6];
uniform vec3 uFigCol[6];
varying vec3 vPos;
${"__NOISE__"}
void main(){
  float n = snoise(vec3(vPos * 0.4 + vec3(0.0, 0.0, uT * 0.02))) * 0.5 + 0.5;
  vec3 paper = mix(vec3(0.97, 0.94, 0.9), vec3(0.93, 0.9, 0.87), n);
  float threadP = smoothstep(4.0, 7.5, uT);
  float weave = smoothstep(7.5, 12.0, uT);
  float hush = smoothstep(14.0, 18.0, uT);
  vec3 col = paper;
  if (uSurface < 0.5) {
    float th = atan(vPos.x, -vPos.z);
    float y = vPos.y;
    // a faint wash of her colours, gathering as the threads weave
    vec3 wash = mix(mix(vec3(0.93, 0.35, 0.6), vec3(0.98, 0.6, 0.25), n), mix(vec3(0.6, 0.8, 0.3), vec3(0.3, 0.5, 0.85), snoise(vec3(vPos * 0.25 + 3.0)) * 0.5 + 0.5), 0.5);
    col = mix(col, wash, 0.1 * weave);
    // threads: from each woman toward the one mind at the front
    for (int i = 0; i < 6; i++){
      float ti = uFigTh[i];
      float s = th / ti;
      if (s < -0.02 || s > 1.05) continue;
      float reach = 1.0 - threadP * 1.05;
      float on = smoothstep(reach - 0.04, reach + 0.02, s) * step(0.001, threadP);
      float conv = smoothstep(0.0, 0.9, s);
      for (int j = 0; j < 4; j++){
        float fj = float(j);
        float yj = 2.55 + (fj - 1.5) * 0.16 * (0.25 + 0.75 * conv) + 0.32 * conv * sin(th * 4.0 + fj * 1.7 + float(i) + uPhase * 0.8);
        float w = 0.012 + 0.008 * conv;
        float ln = 1.0 - smoothstep(w * 0.4, w, abs(y - yj));
        col = mix(col, uFigCol[i], ln * on * (1.0 - 0.75 * hush) * 0.9);
      }
    }
    // the painting's single profile, woven in from the threads at the front
    vec2 tile = vec2(uTileH * uAspect, uTileH) * 0.92;
    vec2 q = vec2(th * uRadius, y - uCy - 0.35) / tile + 0.5;
    if (q.x > 0.0 && q.x < 1.0 && q.y > 0.0 && q.y < 1.0) {
      vec3 paint = texture2D(uMap, mix(uCrop.xy, uCrop.zw, q)).rgb;
      float rowsW = smoothstep(0.0, 1.0, weave * 1.6 - abs(q.x - 0.5) * 1.2 - 0.3 * (0.5 + 0.5 * sin(q.y * 180.0 + q.x * 9.0)));
      float edge = smoothstep(0.0, 0.02, q.x) * smoothstep(0.0, 0.02, 1.0 - q.x) * smoothstep(0.0, 0.02, q.y) * smoothstep(0.0, 0.02, 1.0 - q.y);
      col = mix(col, paint, clamp(rowsW, 0.0, 1.0) * edge);
    }
  } else if (uSurface < 1.5) {
    col = mix(paper * 0.9, paper, smoothstep(0.0, uRadius, length(vPos.xz)));
  } else {
    col = paper * 1.01;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
  figFrag: `
uniform sampler2D uFig;
uniform float uAppear, uThread, uFade, uDir, uT;
varying vec2 vUv;
void main(){
  float a = texture2D(uFig, vUv).a;
  vec3 c = texture2D(uFig, vUv).rgb;
  // hair upward and behind dissolves into fine threads drawn toward the one mind
  for (int j = 1; j < 7; j++){
    float fj = float(j);
    vec2 o = vec2(-uDir * fj * 0.035 * uThread, sin(vUv.x * 18.0 + fj + uT * 0.7) * 0.004 * uThread);
    vec4 s = texture2D(uFig, vUv + o);
    float k = s.a * (1.0 - fj / 7.0) * uThread * smoothstep(0.35, 0.75, vUv.y);
    if (k > a) { a = k; c = s.rgb; }
  }
  a *= uAppear * (1.0 - uFade);
  if (a < 0.01) discard;
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`,
};
